import useCompanies from 'hooks/useCompanies';
import useEquipmentData from 'hooks/useEquipmentData';
import usePeopleData from 'hooks/usePeopleData';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import useOperationProperties from 'screens/Applications&WorkOrders/Components/hooks/useOperationProperties';
import {
  FormValues,
  PersonCompany,
  WeatherData,
  WeatherMode
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import useWorkOrders, {
  WorkOrderDetails
} from 'screens/Applications&WorkOrders/WorkOrders/Hooks/useWorkOrders';
import useApplication, {
  ApplicationDetails,
  ApplicationMethod,
  TimingEvent,
  WorkType,
  WeatherMode as ApplicationWeatherMode
} from 'screens/Applications&WorkOrders/Applications/Hooks/useApplication';
import { useLocation } from 'react-router-dom';
import { useAuth } from 'utilities/base-auth';
import generateDefaultApplicationId from 'screens/Applications&WorkOrders/StepsLayout/Helpers/generateDefaultApplicationId';
import { getInitials } from 'helpers/textHelpers';
import {
  Flow,
  RoutePathState
} from 'screens/Applications&WorkOrders/RouteAdmin';
import { mapWeather, defaultWeatherData } from 'hooks/useWeatherApi';
import getEnumKeyByValue from 'helpers/getEnumKeyByValue';
import {
  getSelectedCropSeasonIds,
  shouldShowOperationCropSeasonMismatch
} from 'helpers/cropSeasonMismatch';
import { recalculateApplicationValues } from 'screens/Applications&WorkOrders/StepsLayout/Components/ProductsStep/components/ProductsTable/helpers';
import { remapProductsForAppliedAcreage } from 'screens/Applications&WorkOrders/StepsLayout/Components/ProductsStep/helpers/remapProductsForAppliedAcreage';
import useMapCropZones from './useMapCropZones';
import useMapProducts from './useMapProducts';

type OperationType = 'application' | 'work-order';
type OperationDetails = ApplicationDetails | WorkOrderDetails;

function useOperationFormById(id: string, operationType: OperationType) {
  const { state } = useLocation<RoutePathState | undefined>();
  const { getWorkOrder } = useWorkOrders();
  const { getApplication: fetchApplication } = useApplication();

  const { user } = useAuth();

  const [loading, setLoading] = useState<boolean>(true);
  const [operationFormikData, setOperationFormikData] = useState<
    FormValues | undefined
  >(undefined);

  const { people, loading: peopleLoading } = usePeopleData();
  const { companies, loading: companiesLoading } = useCompanies();
  const { equipments, loading: equipmentsLoading } = useEquipmentData();
  const {
    propertiesData,
    isLoading: propertiesLoading
  } = useOperationProperties(id, operationType);
  const {
    fetchCropZoneSeasons,
    buildCropZoneMap,
    mapCropZones
  } = useMapCropZones();
  const { buildProductsDataMap, fetchAllPests, mapProducts } = useMapProducts();

  const calculateDuration = useCallback(
    (startDate: string, endDate: string) => {
      const startDateMoment = moment(startDate);
      const endDateMoment = moment(endDate);
      return moment.duration(endDateMoment.diff(startDateMoment));
    },
    []
  );

  const isDuplicateFlow = state?.flow === Flow.DUPLICATE;

  const findAuthorizedBy = useCallback(
    (authorizerId?: string): PersonCompany | undefined => {
      if (!authorizerId) return undefined;

      const authorizationPerson: any = people.find(
        (p: any) => p.id === authorizerId
      );
      if (authorizationPerson) {
        return {
          id: authorizationPerson.id,
          name: authorizationPerson.name,
          licenseNumber: authorizationPerson.licenseNumber,
          type: 'person' as const
        };
      }

      const authorizationCompany: any = companies.find(
        (c: any) => c.id === authorizerId
      );
      if (authorizationCompany) {
        return {
          id: authorizationCompany.id,
          name: authorizationCompany.name,
          type: 'company' as const
        };
      }

      return undefined;
    },
    [people, companies]
  );

  const getOperationEquipments = useCallback(
    (equipmentIds?: string[]) => {
      if (!equipmentIds || equipmentIds.length === 0) {
        return [];
      }

      const filtered = equipments.filter(equipment =>
        equipmentIds.includes(equipment.id)
      );

      // If equipment IDs exist but no matching equipment is found
      // (e.g., fetch was cancelled, data hasn't loaded yet, or IDs don't exist),
      // create placeholder equipment objects with just IDs. The dropdown will
      // handle fetching the full equipment data.
      if (filtered.length === 0) {
        return equipmentIds.map(equipmentId => ({
          id: equipmentId,
          name: 'Loading...', // Temporary name - will be replaced by dropdown
          type: undefined
        }));
      }

      return filtered;
    },
    [equipments]
  );

  const getMappedOperationData = useCallback(
    async (
      operation: OperationDetails,
      cropZoneSeasonsMap: Map<string, Array<{ id: string }>>,
      hasCropSeasonMismatch: boolean
    ) => {
      const cropZoneIdsToKeep = hasCropSeasonMismatch
        ? new Set(cropZoneSeasonsMap.keys())
        : undefined;

      const operationPropertiesToMap = hasCropSeasonMismatch
        ? operation.properties.filter(property =>
            cropZoneIdsToKeep?.has(property.cropZoneId)
          )
        : operation.properties;

      const operationPropertiesDataToMap = hasCropSeasonMismatch
        ? propertiesData.filter(property =>
            cropZoneIdsToKeep?.has(property.cropZoneId)
          )
        : propertiesData;

      const cropZoneMapProp = buildCropZoneMap(operationPropertiesToMap);
      const productsFullDataMap = await buildProductsDataMap(
        operation.products
      );
      const allPests = await fetchAllPests(operation.products);

      const mappedCropZones = mapCropZones(
        cropZoneMapProp,
        cropZoneSeasonsMap,
        operationPropertiesDataToMap
      );

      // Prefer the BE's stored totalAppliedArea — that's the user's original
      // input from when the operation was created/last saved. Re-summing the
      // per-zone appliedAcreage values would re-introduce any floating-point
      // drift baked into them from the distribution math (e.g. 480 → 479.99…),
      // and that drifted total would then round-trip back to BE on save.
      // In the crop-season-mismatch flow we intentionally drop zones that
      // aren't in the selected seasons, so the BE total (which covers ALL
      // zones) would be inconsistent with the displayed/remapped zones and
      // would skew the downstream product + tank recalculations — fall back
      // to the filtered zone sum there.
      const totalAppliedAcreage =
        (!hasCropSeasonMismatch && operation.totalAppliedArea) ||
        mappedCropZones.reduce((sum, cropZone) => {
          return sum + (cropZone.appliedAcreage || 0);
        }, 0) ||
        0;

      const mappedProducts = mapProducts(
        operation.products,
        productsFullDataMap,
        allPests,
        totalAppliedAcreage
      );

      const productsRemappedByArea = hasCropSeasonMismatch
        ? remapProductsForAppliedAcreage(
            mappedProducts,
            totalAppliedAcreage,
            operation.tankInformation?.tankCount
          )
        : mappedProducts;

      const remappedTankInfoValues =
        hasCropSeasonMismatch &&
        operation.tankInformation &&
        operation.tankInformation.tankSizeValue > 0
          ? recalculateApplicationValues(
              {
                products: productsRemappedByArea,
                totalAppliedAcreage,
                tankSize: operation.tankInformation.tankSizeValue,
                tankCount: operation.tankInformation.tankCount,
                overallRatePerAcre: operation.tankInformation.ratePerAcreValue,
                totalProduct: operation.tankInformation.totalProductValue
              } as FormValues,
              'overallRatePerAcre',
              operation.tankInformation.ratePerAcreValue
            )
          : undefined;

      return {
        mappedCropZones,
        totalAppliedAcreage,
        productsRemappedByArea,
        remappedTankInfoValues
      };
    },
    [
      buildCropZoneMap,
      buildProductsDataMap,
      fetchAllPests,
      mapCropZones,
      mapProducts,
      propertiesData
    ]
  );

  const buildApplicationSpecificValues = useCallback(
    (
      application: ApplicationDetails,
      startDateTime: moment.Moment,
      baseFormValues: FormValues
    ): FormValues => {
      const endDateTime = isDuplicateFlow
        ? moment().set({ hour: 10, minute: 0, second: 0 })
        : moment(application.endDate);

      const duration = calculateDuration(
        moment(startDateTime).format(),
        moment(endDateTime).format()
      );
      const durationHours = Math.floor(duration.asHours());
      const durationMinutes = Math.floor(duration.asMinutes() % 60);

      const weatherMode =
        application.weather?.mode === ApplicationWeatherMode.AUTOMATIC
          ? WeatherMode.AUTOMATIC
          : WeatherMode.MANUAL;

      return {
        ...baseFormValues,
        endDateTime,
        durationHours,
        durationMinutes,
        weather: isDuplicateFlow
          ? {
              mode: weatherMode,
              displayAutomaticWeatherModal: false,
              start: { ...defaultWeatherData } as WeatherData,
              end: { ...defaultWeatherData } as WeatherData
            }
          : {
              mode: weatherMode,
              displayAutomaticWeatherModal:
                application.weather?.displayAutomaticWeatherModal,
              ...(mapWeather(application.weather, 'BE_TO_FE') as {
                start: WeatherData;
                end: WeatherData;
              })
            }
      };
    },
    [calculateDuration, isDuplicateFlow]
  );

  const mapToOperationFormik = useCallback(
    async (
      operation: OperationDetails,
      cropZoneSeasonsMap: Map<string, Array<{ id: string }>>,
      hasCropSeasonMismatch = false
    ): Promise<FormValues> => {
      const startDateTime = isDuplicateFlow
        ? moment().set({ hour: 8, minute: 0, second: 0 })
        : moment(operation.startDate);

      const authorizedBy = findAuthorizedBy(
        operation.authorization?.authorizerId
      );
      const equipmentsForForm = getOperationEquipments(operation.equipmentIds);

      const {
        mappedCropZones,
        totalAppliedAcreage,
        productsRemappedByArea,
        remappedTankInfoValues
      } = await getMappedOperationData(
        operation,
        cropZoneSeasonsMap,
        hasCropSeasonMismatch
      );

      const applicationId = isDuplicateFlow
        ? generateDefaultApplicationId(getInitials(user?.name ?? ''))
        : operation.name;

      const instanceApplicationId = isDuplicateFlow ? undefined : operation.id;

      // Base form values common to both applications and work-orders
      const baseFormValues: FormValues = {
        applicationId,
        startDateTime,
        id: instanceApplicationId,
        workType: getEnumKeyByValue(WorkType, operation.type)!,
        timingEvent: getEnumKeyByValue(TimingEvent, operation.timingEvent),
        authorizationDate: moment(operation.authorization.authorizationDate),
        authorizedBy,
        applicationMethod: getEnumKeyByValue(
          ApplicationMethod,
          operation.applicationMethod
        ),
        equipments: equipmentsForForm,
        tagIds: operation.tagIds || [],
        applicators: operation.applicators || [{ personId: '', companyId: '' }],
        notes: operation.notes,
        cropZones: mappedCropZones,
        totalAppliedAcreage,
        totalCost: operation.totalCost || 0,
        products: remappedTankInfoValues?.products || productsRemappedByArea,
        isTankInfoEnabled: !!operation.tankInformation,
        tankCount:
          remappedTankInfoValues?.tankCount ??
          operation.tankInformation?.tankCount ??
          undefined,
        tankSize: operation.tankInformation?.tankSizeValue || undefined,
        overallRatePerAcre:
          operation.tankInformation?.ratePerAcreValue || undefined,
        totalProduct:
          remappedTankInfoValues?.totalProduct ??
          operation.tankInformation?.totalProductValue ??
          undefined,
        isCarrierRowHidden: operation.products.some(
          p => p.isCarrier && p.isHidden
        )
      };

      if (operationType === 'application') {
        return buildApplicationSpecificValues(
          operation as ApplicationDetails,
          startDateTime,
          baseFormValues
        );
      }

      // Work-orders don't have endDateTime, duration, or weather fields
      // Set defaults for these fields
      return {
        ...baseFormValues,
        endDateTime: undefined,
        durationHours: undefined,
        durationMinutes: undefined
      };
    },
    [
      operationType,
      isDuplicateFlow,
      buildApplicationSpecificValues,
      findAuthorizedBy,
      getMappedOperationData,
      getOperationEquipments,
      user?.name
    ]
  );

  const getOperationData = useCallback(async () => {
    let operation = state?.operation;

    if (!operation) {
      if (operationType === 'application') {
        operation = await fetchApplication(id);
      } else {
        operation = await getWorkOrder(id);
      }
    }

    const selectedCropSeasonIds = getSelectedCropSeasonIds();
    const hasCropSeasonMismatch = shouldShowOperationCropSeasonMismatch(
      operation,
      selectedCropSeasonIds
    );
    const cropSeasonIdsToUse = hasCropSeasonMismatch
      ? operation.cropSeasonIds.filter(cropSeasonId =>
          selectedCropSeasonIds.includes(cropSeasonId)
        )
      : operation.cropSeasonIds;

    const cropZoneSeasonsMap = await fetchCropZoneSeasons(cropSeasonIdsToUse);
    const formikValues = await mapToOperationFormik(
      operation,
      cropZoneSeasonsMap,
      hasCropSeasonMismatch
    );
    setOperationFormikData(formikValues);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    operationType,
    getWorkOrder,
    fetchApplication,
    state,
    mapToOperationFormik,
    fetchCropZoneSeasons
  ]);

  useEffect(() => {
    if (
      peopleLoading ||
      companiesLoading ||
      equipmentsLoading ||
      propertiesLoading
    ) {
      return;
    }
    getOperationData();
  }, [
    getOperationData,
    peopleLoading,
    companiesLoading,
    equipmentsLoading,
    propertiesLoading
  ]);

  return {
    operationFormikData,
    loading
  };
}

export default useOperationFormById;
